/**
 * /admin/data-chat — Session 88 pt7 (P0).
 *
 * Slack-style chat window. Streams Claude Sonnet 4.5 over Server-Sent
 * Events. Renders tool-use events as collapsible inline cards so the
 * operator can see exactly which SQL Claude ran and what came back.
 *
 * Persistence: session-only (per user choice) — refresh clears the
 * conversation. Future: add a "Save thread" button to write to a
 * data_chat_threads table.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const API = process.env.REACT_APP_BACKEND_URL;

/** Read a cookie value by name. Mirrors `readCookie` in lib/api.js —
 *  the chat uses native `fetch` (not the axios `api` instance) so we
 *  can stream the SSE response, which means we have to attach the
 *  double-submit CSRF token (`eyvo_csrf`) manually on writes. */
function readCookie(name) {
  const m = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name + "=([^;]*)")
  );
  return m ? decodeURIComponent(m[1]) : null;
}

export default function DataChatPage() {
  const [config, setConfig] = useState(null);
  const [totals, setTotals] = useState(null);
  const [messages, setMessages] = useState([]); // [{role, content, blocks?}]
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const loadTotals = () =>
    fetch(`${API}/api/admin/data-chat/usage-totals`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setTotals(d))
      .catch(() => {});

  useEffect(() => {
    fetch(`${API}/api/admin/data-chat/config`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setConfig)
      .catch(() => toast.error("You don't have permission to use the data chat."));
    loadTotals();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text || streaming) return;

    // Snapshot history for the API + append user msg + placeholder assistant msg
    const newUserMsg = { role: "user", content: text };
    const apiHistory = [...messages.map(stripBlocks), newUserMsg];
    setMessages(m => [...m, newUserMsg, {
      role: "assistant", content: "", blocks: [], _streaming: true,
    }]);
    setDraft("");
    setStreaming(true);

    try {
      const csrf = readCookie("eyvo_csrf");
      const resp = await fetch(`${API}/api/admin/data-chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ messages: apiHistory }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `HTTP ${resp.status}`);
      }
      await consumeSSE(resp, (event, data) => {
        setMessages(m => updateLastAssistant(m, event, data));
      });
    } catch (e) {
      setMessages(m => updateLastAssistant(m, "error",
        { message: e.message || String(e) }));
      toast.error(e.message || "Chat stream failed");
    } finally {
      setStreaming(false);
      setMessages(m => {
        const cp = [...m];
        if (cp.length && cp[cp.length - 1].role === "assistant") {
          cp[cp.length - 1] = { ...cp[cp.length - 1], _streaming: false };
        }
        return cp;
      });
      // Refresh the daily/monthly running totals after each turn.
      loadTotals();
    }
  };

  const reset = () => {
    if (streaming) return;
    setMessages([]);
  };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6 flex flex-col h-[calc(100vh-120px)]">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">
            Admin · Data chat
          </div>
          <h1 className="text-3xl font-semibold mt-1 text-neutral-900">
            Ask the data
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Claude Sonnet 4.5 with live SQL + SendGrid + system-health
            tools. Session-only (refresh clears the thread).
            {config?.billing_access && (
              <span className="ml-2 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">
                Billing access enabled for you
              </span>
            )}
          </p>
          {totals && (
            <div
              data-testid="chat-totals-pill"
              className="mt-3 inline-flex items-center gap-3 text-[11px] uppercase tracking-wider text-neutral-500 border border-neutral-200 rounded-full px-3 py-1.5 bg-white"
            >
              <span>
                Today{" "}
                <span className="font-bold text-neutral-900">
                  {fmtCost(totals.today.cost_usd)}
                </span>
                <span className="text-neutral-400 ml-1">
                  ({totals.today.turns}{" "}
                  {totals.today.turns === 1 ? "turn" : "turns"})
                </span>
              </span>
              <span className="text-neutral-300">·</span>
              <span>
                This month{" "}
                <span className="font-bold text-neutral-900">
                  {fmtCost(totals.month.cost_usd)}
                </span>
                <span className="text-neutral-400 ml-1">
                  ({totals.month.turns}{" "}
                  {totals.month.turns === 1 ? "turn" : "turns"})
                </span>
              </span>
              <span className="text-neutral-300">·</span>
              <span className="text-neutral-400 normal-case tracking-normal">
                Anthropic {totals.model}
              </span>
            </div>
          )}
        </div>

        <div
          ref={scrollRef}
          data-testid="chat-scroll"
          className="flex-1 overflow-y-auto border border-neutral-200 rounded bg-white p-4 space-y-4"
        >
          {messages.length === 0 && <EmptyState config={config} onPick={setDraft} />}
          {messages.map((m, i) => (
            <Message key={i} msg={m} />
          ))}
        </div>

        <div className="mt-4 flex gap-2">
          <Textarea
            data-testid="chat-input"
            placeholder={
              streaming
                ? "Claude is replying…"
                : "Ask anything — e.g. 'How many drip emails went out yesterday?'"
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            disabled={streaming}
            className="flex-1 min-h-[60px] resize-none"
          />
          <div className="flex flex-col gap-2">
            <Button
              data-testid="chat-send"
              onClick={send}
              disabled={streaming || !draft.trim()}
              className="bg-neutral-900 hover:bg-neutral-800"
            >
              {streaming ? "…" : "Send"}
            </Button>
            <Button
              data-testid="chat-reset"
              onClick={reset}
              disabled={streaming || messages.length === 0}
              variant="outline"
            >
              Reset
            </Button>
          </div>
        </div>
        <div className="mt-2 text-xs text-neutral-400">
          {config && (
            <>Model: <code>{config.model}</code> · cap{" "}
            {config.max_tokens.toLocaleString()} tokens, max{" "}
            {config.max_tool_iterations} tool calls per turn · ⌘/Ctrl+Enter to send</>
          )}
        </div>
      </div>
    </AppLayout>
  );
}


function EmptyState({ config, onPick }) {
  const examples = useMemo(() => {
    const base = [
      "How many drip emails went out yesterday?",
      "Show the last 10 login attempts with their cities.",
      "Run the marketing health snapshot now.",
      "Which workflows have zero enrollments due in the next 7 days?",
    ];
    if (config?.billing_access) {
      base.unshift("Find Wilson Elser and show their next renewal.");
      base.push("Which quotes were sent in the last 30 days but not accepted?");
    }
    return base;
  }, [config]);
  return (
    <div data-testid="chat-empty" className="text-center py-16 text-neutral-500 text-sm">
      <div className="text-neutral-400 mb-4">Start a conversation. Try one of:</div>
      <div className="flex flex-col gap-2 items-center max-w-md mx-auto">
        {examples.map((q, i) => (
          <button
            key={i}
            data-testid={`example-${i}`}
            onClick={() => onPick(q)}
            className="text-left text-sm px-3 py-2 rounded border border-neutral-200 hover:bg-neutral-50 w-full text-neutral-700"
          >
            &ldquo;{q}&rdquo;
          </button>
        ))}
      </div>
    </div>
  );
}


function Message({ msg }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[70%] bg-neutral-900 text-neutral-50 rounded-2xl rounded-br-sm px-4 py-2 text-sm whitespace-pre-wrap">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] w-full">
        {(msg.blocks || []).map((b, i) => (
          <BlockView key={i} block={b} />
        ))}
        {msg.content && (
          <div className="prose prose-sm max-w-none text-neutral-900">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
          </div>
        )}
        {msg._streaming && (
          <div className="text-xs text-neutral-400 mt-1">▎ thinking…</div>
        )}
        {msg.usage && !msg._streaming && <UsageFooter usage={msg.usage} />}
      </div>
    </div>
  );
}


function UsageFooter({ usage }) {
  const tokens = (usage.input_tokens || 0) + (usage.output_tokens || 0);
  const secs = usage.elapsed_ms != null ? (usage.elapsed_ms / 1000).toFixed(1) : null;
  return (
    <div
      data-testid="chat-usage"
      className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-400 border border-neutral-200 rounded-full px-2.5 py-1 bg-neutral-50"
    >
      <span className="font-bold text-neutral-700">{fmtCost(usage.cost_usd)}</span>
      <span className="text-neutral-300">·</span>
      <span>
        {usage.input_tokens?.toLocaleString() ?? 0} in /{" "}
        {usage.output_tokens?.toLocaleString() ?? 0} out
      </span>
      <span className="text-neutral-300">·</span>
      <span>{usage.iterations} {usage.iterations === 1 ? "turn" : "turns"}</span>
      {secs && (
        <>
          <span className="text-neutral-300">·</span>
          <span>{secs}s</span>
        </>
      )}
    </div>
  );
}


function fmtCost(cost) {
  const v = cost ?? 0;
  if (v >= 0.01) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(4)}`;
}


function BlockView({ block }) {
  if (block.type === "tool_use") {
    const argText = block.input?.sql
      || JSON.stringify(block.input || {}, null, 2);
    return (
      <details className="mb-2 bg-neutral-50 border border-neutral-200 rounded px-3 py-2 text-xs">
        <summary className="cursor-pointer text-neutral-700">
          <span className="font-mono text-neutral-900">{block.name}</span>
          {block.result && (
            <span className={
              "ml-2 px-1.5 py-0.5 rounded " +
              (block.result.ok === false
                ? "bg-red-100 text-red-800"
                : "bg-emerald-100 text-emerald-800")
            }>
              {block.result.ok === false ? "error" : (block.result.summary || "ok")}
            </span>
          )}
        </summary>
        <pre className="mt-2 text-[11px] whitespace-pre-wrap font-mono text-neutral-700">
          {argText}
        </pre>
        {block.result && (
          <div className="mt-2 text-[11px] text-neutral-500">
            {block.result.summary || "complete"}
            {block.result.took_ms != null && ` · ${block.result.took_ms}ms`}
          </div>
        )}
      </details>
    );
  }
  if (block.type === "error") {
    return (
      <div className="mb-2 bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-800">
        {block.message}
      </div>
    );
  }
  return null;
}


// ─── SSE consumer (Fetch-based; EventSource doesn't support POST) ──


async function consumeSSE(resp, onEvent) {
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const lines = chunk.split("\n");
      let event = "message";
      let data = "";
      for (const ln of lines) {
        if (ln.startsWith("event:")) event = ln.slice(6).trim();
        else if (ln.startsWith("data:")) data += ln.slice(5).trim();
      }
      if (!data) continue;
      try {
        onEvent(event, JSON.parse(data));
      } catch {
        // ignore
      }
    }
  }
}


function updateLastAssistant(messages, event, data) {
  const cp = [...messages];
  const last = cp[cp.length - 1];
  if (!last || last.role !== "assistant") return cp;

  if (event === "text") {
    cp[cp.length - 1] = { ...last, content: (last.content || "") + (data.delta || "") };
  } else if (event === "tool_use") {
    const blocks = [...(last.blocks || []),
      { type: "tool_use", name: data.name, input: data.input }];
    cp[cp.length - 1] = { ...last, blocks };
  } else if (event === "tool_result") {
    const blocks = [...(last.blocks || [])];
    // Attach result to the most recent matching tool_use without a result
    for (let i = blocks.length - 1; i >= 0; i--) {
      if (blocks[i].type === "tool_use" && blocks[i].name === data.name
          && !blocks[i].result) {
        blocks[i] = { ...blocks[i], result: data };
        break;
      }
    }
    cp[cp.length - 1] = { ...last, blocks };
  } else if (event === "error") {
    const blocks = [...(last.blocks || []),
      { type: "error", message: data.message || "Unknown error" }];
    cp[cp.length - 1] = { ...last, blocks };
  } else if (event === "usage") {
    cp[cp.length - 1] = { ...last, usage: data };
  }
  return cp;
}


function stripBlocks(m) {
  // The API only needs role + content text; strip the blocks metadata.
  return { role: m.role, content: m.content || "" };
}
